import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import api from "@api/api.js";
import t from "@/services/translationService.js";
import { formatDateTime } from "@/services/formatDate.js";

// Rows per page. The logs table only grows, so the panel must never try to
// render all of it — a few thousand alerts would lock the browser up.
const PAGE_SIZE = 50;

// The filter picks ONE severity, or "all". Not "this level and above": with
// that, picking "Warning" on a database of errors showed every error row and
// looked like the filter was doing nothing at all.
// The `type` column holds machine identifiers (securityLog.js's vocabulary, plus
// the older generic types). They are what you would grep the logs database for,
// but they read badly in a dashboard — and worse in German, where an English
// snake_case identifier sits untranslated among translated text.
//
// Mapped explicitly rather than derived from the string, so an event whose label
// should not be a literal translation of its id ("auth.token_reuse" reads far
// better as "Session stolen") can say what it means.
const TYPE_LABEL_KEYS = {
  "auth.login_failed": "cockpit.alerts.types.loginFailed",
  "auth.login_blocked": "cockpit.alerts.types.loginBlocked",
  "auth.token_reuse": "cockpit.alerts.types.tokenReuse",
  "contact.blocked": "cockpit.alerts.types.contactBlocked",
  "boot.env_invalid": "cockpit.alerts.types.bootEnvInvalid",
  error: "cockpit.alerts.types.error",
  client_error: "cockpit.alerts.types.clientError",
  database: "cockpit.alerts.types.database",
  system: "cockpit.alerts.types.system",
  auth: "cockpit.alerts.types.auth",
  image: "cockpit.alerts.types.image",
};

/**
 * Falls back to the raw identifier for anything unmapped — a new event type
 * added later shows up legibly instead of as a blank or a missing-key path.
 */
function typeLabel(type) {
  if (!type) return "—";
  const key = TYPE_LABEL_KEYS[type];
  return key ? t.t(key, type) : type;
}

const ALL = "all";
const LEVELS = [
  { value: ALL, labelKey: "cockpit.alerts.levelAll" },
  { value: "40", labelKey: "cockpit.alerts.levelWarn" },
  { value: "50", labelKey: "cockpit.alerts.levelError" },
  { value: "60", labelKey: "cockpit.alerts.levelFatal" },
];

/**
 * Cockpit → Alert Manager. Shows the application log rows written at warn
 * severity or above (GET /api/alerts), newest first, filtered by severity and
 * paged 50 at a time.
 *
 * There is no separate alert store — the log stream this app already writes is
 * the source of truth, so anything logged at warn/error/fatal shows up here.
 *
 * props: { lang }
 */
export default class AlertManagerPanel extends CockpitPanel {
  constructor(props) {
    super(props);
    this.state = {
      alerts: [],
      counts: null,
      total: 0,
      level: ALL,
      page: 0,
      error: "",
      expanded: new Set(),
    };
  }

  async onBeforeMount() {
    Object.assign(this.state, await this.fetchData(this.state.level, this.state.page));
  }

  async fetchData(level, page) {
    const query = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (level !== ALL) query.set("level", level);

    try {
      const res = await api.get(`/api/alerts?${query}`);
      return { alerts: res.alerts || [], counts: res.counts || null, total: res.total || 0, error: "" };
    } catch (err) {
      return { alerts: [], counts: null, total: 0, error: err.message || t.t("cockpit.alerts.loadError") };
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.alerts"));
  }

  lead() {
    return Component.escape(t.t("cockpit.alerts.lead"));
  }

  actions() {
    const options = LEVELS.map(
      (level) =>
        `<option value="${level.value}" ${level.value === this.state.level ? "selected" : ""}>${Component.escape(t.t(level.labelKey))}</option>`
    ).join("");
    return `
      <label class="cockpit-filter">
        <span class="cockpit-filter__label">${Component.escape(t.t("cockpit.alerts.severity"))}</span>
        <select class="cockpit-filter__select" data-action="level">${options}</select>
      </label>
      <button type="button" class="btn-secondary" data-action="refresh">${Component.escape(t.t("common.refresh"))}</button>`;
  }

  #pageCount() {
    return Math.max(1, Math.ceil(this.state.total / PAGE_SIZE));
  }

  /** Hidden entirely when everything fits on one page. */
  #pager() {
    const { total, page } = this.state;
    if (total <= PAGE_SIZE) return "";

    const pages = this.#pageCount();
    const label = t
      .t("cockpit.alerts.pageOf")
      .replace("{page}", String(page + 1))
      .replace("{pages}", String(pages))
      .replace("{total}", String(total));

    return `
      <div class="cockpit-pager">
        <!-- Words, not icons: row actions are icon-only (rowActions.js) because a
           table repeats them once per row, but a pager appears once and reads
           as navigation. "‹ Prev" survives translation better than a glyph. -->
      <button type="button" class="btn-secondary btn-sm" data-page="prev" ${page === 0 ? "disabled" : ""}>
          ${Component.escape(t.t("cockpit.alerts.prevPage"))}
        </button>
        <span class="cockpit-pager__label">${Component.escape(label)}</span>
        <button type="button" class="btn-secondary btn-sm" data-page="next" ${page >= pages - 1 ? "disabled" : ""}>
          ${Component.escape(t.t("cockpit.alerts.nextPage"))}
        </button>
      </div>`;
  }

  body() {
    if (this.state.error) return CockpitPanel.error(this.state.error);

    const { counts, alerts } = this.state;
    const summary = counts
      ? `<div class="cockpit-chips">
           <span class="status-chip status-chip--warn">${Component.escape(t.t("cockpit.alerts.levelWarn"))} · ${counts.warn}</span>
           <span class="status-chip status-chip--error">${Component.escape(t.t("cockpit.alerts.levelError"))} · ${counts.error}</span>
           <span class="status-chip status-chip--fatal">${Component.escape(t.t("cockpit.alerts.levelFatal"))} · ${counts.fatal}</span>
         </div>`
      : "";

    if (!alerts.length) {
      // "Nothing at this severity" and "nothing at all" are different answers,
      // and only one of them means the filter is hiding something.
      const message = this.state.level === ALL ? t.t("cockpit.alerts.empty") : t.t("cockpit.alerts.emptyFiltered");
      return `${summary}${CockpitPanel.empty(message)}`;
    }

    const rows = alerts
      .map((alert) => {
        const when = formatDateTime(alert.timestamp, this.props.lang);
        const isOpen = this.state.expanded.has(alert.id);
        const detail =
          alert.error && isOpen
            ? `<tr class="cockpit-alert__detail"><td colspan="5"><pre>${Component.escape(alert.error)}</pre></td></tr>`
            : "";
        const toggle = alert.error
          ? `<button type="button" class="cockpit-alert__toggle" data-toggle="${Component.escape(alert.id)}">${Component.escape(isOpen ? t.t("cockpit.alerts.hideDetail") : t.t("cockpit.alerts.showDetail"))}</button>`
          : "";
        return `
          <tr>
            <td><span class="status-chip status-chip--${Component.escape(alert.levelName)}">${Component.escape(alert.levelName)}</span></td>
            <td class="cockpit-row__nowrap">${Component.escape(when)}</td>
            <td title="${Component.escape(alert.type || "")}">${Component.escape(typeLabel(alert.type))}</td>
            <td>
              <span class="cockpit-row__name">${Component.escape(alert.message || "—")}</span>
              ${toggle}
            </td>
            <td>${Component.escape(alert.user || "—")}</td>
          </tr>
          ${detail}`;
      })
      .join("");

    return `
      ${summary}
      <div class="cockpit-table__wrap">
        <table class="cockpit-table">
          <thead>
            <tr>
              <th>${Component.escape(t.t("cockpit.alerts.colLevel"))}</th>
              <th>${Component.escape(t.t("cockpit.alerts.colTime"))}</th>
              <th>${Component.escape(t.t("cockpit.alerts.colType"))}</th>
              <th>${Component.escape(t.t("cockpit.alerts.colMessage"))}</th>
              <th>${Component.escape(t.t("cockpit.alerts.colUser"))}</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${this.#pager()}`;
  }

  onMounted() {
    const refresh = this.$('[data-action="refresh"]');
    if (refresh) this.listen(refresh, "click", () => this.reload());

    const select = this.$('[data-action="level"]');
    if (select) {
      // Back to page 1: page 7 of the unfiltered list rarely exists once the
      // list is narrowed, and landing on an empty page reads as a broken filter.
      this.listen(select, "change", () => this.load(select.value, 0));
    }

    this.$$("[data-page]").forEach((btn) =>
      this.listen(btn, "click", () => {
        const next = btn.getAttribute("data-page") === "next" ? this.state.page + 1 : this.state.page - 1;
        this.load(this.state.level, Math.min(Math.max(next, 0), this.#pageCount() - 1));
      })
    );

    this.$$("[data-toggle]").forEach((btn) =>
      this.listen(btn, "click", () => {
        const id = btn.getAttribute("data-toggle");
        const expanded = new Set(this.state.expanded);
        if (expanded.has(id)) expanded.delete(id);
        else expanded.add(id);
        this.setState({ expanded });
      })
    );
  }

  /** Fetch one filter/page combination and render it. */
  async load(level, page) {
    let data = await this.fetchData(level, page);
    let landed = page;

    // The page can fall off the end between renders — logs get pruned, or a
    // refresh lands after the table shrank. Snap to the last real page rather
    // than showing an empty one that looks like a broken filter.
    if (page > 0 && !data.alerts.length && data.total > 0) {
      landed = Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1);
      data = await this.fetchData(level, landed);
    }

    // Expanded rows are keyed by id, and none of this page's ids carry over.
    this.setState({ level, page: landed, expanded: new Set(), ...data });
  }

  async reload() {
    await this.load(this.state.level, this.state.page);
  }
}
